import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import { ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CountryFlagSvg } from './CountryFlagSvg';

/**
 * Dropdown de país (bandeira + nome + DDI) com busca via cmdk.
 */
export function CountrySelect({
  countries,
  selectedRow,
  open,
  onOpenChange,
  onSelectCountry,
  searchPlaceholder,
  emptyText,
  triggerAriaLabel,
  hasError
}) {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'intl-country-trigger flex h-11 min-w-[5.75rem] shrink-0 items-center gap-2 px-2.5 text-left text-sm text-white/95 outline-none transition-colors',
            'focus-visible:z-[1] focus-visible:ring-1 focus-visible:ring-yellow-500/45',
            hasError && 'ring-1 ring-red-400/35'
          )}
          aria-label={triggerAriaLabel}
          aria-expanded={open}
          aria-haspopup="listbox"
        >
          {selectedRow && (
            <CountryFlagSvg iso={selectedRow.iso} title={selectedRow.name} size="md" />
          )}
          <span className="tabular-nums font-medium tracking-tight text-white/90">
            +{(selectedRow && selectedRow.dialCode) || '—'}
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 text-white/40" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        collisionPadding={12}
        className={cn(
          'intl-country-popover z-[100] w-[min(22rem,calc(100vw-1rem))] border border-white/10 bg-zinc-950/98 p-0 text-white shadow-xl backdrop-blur-md',
          'max-h-[min(70vh,24rem)]'
        )}
      >
        <Command
          className="intl-country-command rounded-md border-0 bg-transparent text-white [&_[cmdk-input-wrapper]]:border-white/10"
          label={searchPlaceholder}
          loop
        >
          <CommandInput
            placeholder={searchPlaceholder}
            className="text-white placeholder:text-white/40"
          />
          <CommandList className="intl-country-list max-h-[min(55vh,18rem)] overflow-y-auto overscroll-contain">
            <CommandEmpty className="py-6 text-center text-sm text-white/50">{emptyText}</CommandEmpty>
            <CommandGroup className="p-1.5">
              {countries.map((c) => (
                <CommandItem
                  key={c.iso}
                  value={`${c.name} ${c.dialCode} ${c.iso}`}
                  keywords={[c.name, c.dialCode, `+${c.dialCode}`, c.iso, c.searchText]}
                  onSelect={() => onSelectCountry(c.iso)}
                  className={cn(
                    'intl-country-item cursor-pointer gap-3 rounded-md px-2 py-2.5 text-sm',
                    'data-[selected=true]:!bg-amber-500/15 data-[selected=true]:!text-white',
                    '[&_.intl-flag-svg_svg]:!h-full [&_.intl-flag-svg_svg]:!w-full [&_.intl-flag-svg_svg]:!max-h-none [&_.intl-flag-svg_svg]:!max-w-none'
                  )}
                >
                  <CountryFlagSvg iso={c.iso} title={c.name} size="sm" />
                  <span className="min-w-0 flex-1 truncate font-medium text-white/92">{c.name}</span>
                  <span className="shrink-0 tabular-nums text-[0.8125rem] text-white/50">+{c.dialCode}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
